/**
 * OrderDetailDrawer — full authoritative view of a single order.
 * -----------------------------------------------------------------------------
 * Renders all fields captured on the order record (line items with snapshot
 * prices, buyer, addresses, price breakdown with coupon, PayPal capture
 * data, fulfillment editor, status timeline, admin notes).
 *
 * IMPORTANT: uses `formatMoney(amount, currency)` — NEVER `format(inr)` — so
 * amounts stored in USD render in USD, no conversion. This is the bug that
 * made Elizabeth's $198 order show as $1.99.
 */
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, RefreshCw, Loader2, User, MapPin, CreditCard, Package,
  ShoppingBag, ClipboardList, Star,
} from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import { resolveImage } from "@/context/PricesContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FULFILL_OPTIONS = [
  "pending", "processing", "in_production", "ready_to_ship",
  "shipped", "out_for_delivery", "delivered", "cancelled",
];
const COURIERS = ["", "Delhivery", "Bluedart", "FedEx", "DHL", "Aramex", "UPS"];

export const OrderDetailDrawer = ({ order, open, token, onClose, onPatch }) => {
  const { formatMoney } = useCurrency();
  const [resyncing, setResyncing] = useState(false);
  const [notes, setNotes] = useState(order?.admin_notes || order?.admin_note || "");
  const [savingNotes, setSavingNotes] = useState(false);

  if (!order) return null;

  const cur = order.currency || "USD";
  const money = (v) => formatMoney(v || 0, cur);
  const paidMoney = (v) => formatMoney(v || 0, order.paid_currency || cur);

  const subtotal = Number(order.subtotal || 0);
  const discount = Number(order.discount || order.coupon?.discount || 0);
  const shipping = Number(order.shipping || 0);
  const tax = Number(order.tax || 0);
  const calculatedTotal = Number(order.total ?? (subtotal - discount + shipping + tax));
  const paid = order.paid_amount != null ? Number(order.paid_amount) : null;
  const paidMismatch = paid != null && Math.abs(paid - calculatedTotal) > 0.01;
  const refunded = Number(order.refunded_amount || 0);

  const resyncPayPal = async () => {
    setResyncing(true);
    try {
      const r = await fetch(`${API}/admin/orders/${order.order_id}/resync-paypal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Resync failed");
      toast.success(`Synced from PayPal · ${(data.changes || []).length} fields updated`);
      onPatch?.(data.order);
    } catch (e) {
      toast.error(e.message || "PayPal resync failed");
    } finally {
      setResyncing(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const r = await fetch(`${API}/admin/orders/${order.order_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ admin_notes: notes }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Save failed");
      toast.success("Note saved");
      onPatch?.(data);
    } catch (e) {
      toast.error(e.message || "Could not save note");
    } finally {
      setSavingNotes(false);
    }
  };

  const patchField = async (patch) => {
    try {
      const r = await fetch(`${API}/admin/orders/${order.order_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Update failed");
      toast.success("Order updated");
      onPatch?.(data);
    } catch (e) {
      toast.error(e.message || "Update failed");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-black/50"
            data-testid="order-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 220, damping: 30 }}
            role="dialog" aria-modal="true"
            data-testid="order-detail-drawer"
            className="fixed inset-y-0 right-0 z-[81] w-full md:max-w-[720px] bg-bone shadow-2xl overflow-y-auto"
          >
            <header className="sticky top-0 z-10 bg-bone border-b border-hairline px-5 py-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="overline text-forest/60 text-[11px] tracking-[3px]">Order</div>
                <div className="font-display text-forest text-2xl truncate" data-testid="order-drawer-id">{order.order_id}</div>
                <div className="text-forest/60 text-xs">{order.created_at ? new Date(order.created_at).toLocaleString() : "—"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {order.gateway === "paypal" && order.gateway_order_id && (
                  <button
                    type="button"
                    onClick={resyncPayPal}
                    disabled={resyncing}
                    data-testid="order-drawer-resync"
                    className="inline-flex items-center gap-2 overline text-forest border border-hairline rounded-full px-3 py-1.5 text-[10px] tracking-[2px] hover:border-forest transition-colors disabled:opacity-50"
                    title="Pull the actual capture amount + shipping from PayPal"
                  >
                    {resyncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    Sync PayPal
                  </button>
                )}
                <button
                  type="button" onClick={onClose} aria-label="Close"
                  data-testid="order-drawer-close"
                  className="w-9 h-9 rounded-full hover:bg-beige flex items-center justify-center text-forest"
                >
                  <X size={16} />
                </button>
              </div>
            </header>

            <div className="px-5 py-5 space-y-6">
              {/* Amount charged banner — highlights any mismatch */}
              {paid != null && (
                <div
                  data-testid="order-paid-banner"
                  className={`rounded-lg px-4 py-3 border ${paidMismatch ? "border-amber-500/60 bg-amber-50" : "border-emerald-500/40 bg-emerald-50"}`}
                >
                  <div className="overline text-[10px] tracking-[3px] text-forest/70">Actual amount charged by PayPal</div>
                  <div className="font-display text-forest text-2xl tabnum mt-1" data-testid="order-paid-amount">
                    {paidMoney(paid)}
                  </div>
                  {paidMismatch && (
                    <div className="text-amber-800 text-xs mt-2">
                      Website calculated {money(calculatedTotal)} but PayPal captured {paidMoney(paid)}. Confirm the shopper's cart snapshot matches — refund the difference if this is a duplicate/legacy charge.
                    </div>
                  )}
                </div>
              )}

              <Section icon={User} title="Customer information">
                <Row label="Name" value={order.buyer?.name || order.paypal_payer_name} />
                <Row label="Email" value={order.buyer?.email || order.paypal_payer_email} />
                <Row label="Phone" value={order.buyer?.phone} />
                <Row label="Country" value={order.buyer?.country} />
              </Section>

              <Section icon={MapPin} title="Shipping information">
                {order.shipping_address ? (
                  <div className="text-forest text-sm leading-relaxed" data-testid="order-shipping-address">
                    {order.shipping_address.name && <div>{order.shipping_address.name}</div>}
                    {order.shipping_address.line1 && <div>{order.shipping_address.line1}</div>}
                    {order.shipping_address.line2 && <div>{order.shipping_address.line2}</div>}
                    <div>
                      {[order.shipping_address.city, order.shipping_address.state, order.shipping_address.postal_code]
                        .filter(Boolean).join(", ")}
                    </div>
                    {order.shipping_address.country && <div className="uppercase">{order.shipping_address.country}</div>}
                  </div>
                ) : (
                  <div className="text-forest/50 text-sm italic">No shipping address on file. Click <b>Sync PayPal</b> to fetch it if this was a PayPal order.</div>
                )}
                {order.billing_address && (
                  <div className="mt-3 pt-3 border-t border-hairline">
                    <div className="overline text-forest/60 text-[10px] mb-1">Billing</div>
                    <div className="text-forest text-sm">{formatAddress(order.billing_address)}</div>
                  </div>
                )}
              </Section>

              <Section icon={ShoppingBag} title={`Items · ${(order.items || []).length}`}>
                <div className="space-y-3">
                  {(order.items || []).map((it, i) => (
                    <div key={i} className="flex gap-3 items-start" data-testid={`order-item-${i}`}>
                      <div className="w-16 h-20 rounded overflow-hidden bg-beige shrink-0 border border-hairline">
                        {it.image ? (
                          <img src={resolveImage(it.image)} alt={it.title || "Product"} loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-forest/30"><Package size={18} /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-forest font-medium truncate">{it.title || it.slug}</div>
                        <div className="text-forest/60 text-xs mt-1 space-x-2">
                          {it.slug && <span>SKU: {it.slug}</span>}
                          {it.size && <span>· Size: {it.size}</span>}
                          {it.colour && <span>· Colour: {it.colour}</span>}
                          {it.variation?.fabric && <span>· Fabric: {it.variation.fabric}</span>}
                        </div>
                        {it.summary && <div className="text-forest/70 text-xs mt-1 italic">{it.summary}</div>}
                        {it.variation?.custom_note && (
                          <div className="text-forest/70 text-xs mt-1">Custom: {it.variation.custom_note}</div>
                        )}
                        {it.variation?.monogram && (
                          <div className="text-forest/70 text-xs mt-1">Monogram: “{it.variation.monogram.text || ""}” · {it.variation.monogram.font || ""}</div>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs">
                          <span className="text-forest/60">Qty:</span>
                          <span className="text-forest tabnum">{it.qty}</span>
                          <span className="text-forest/60">×</span>
                          <span className="text-forest tabnum" data-testid={`order-item-${i}-unit`}>{money(it.unit_price)}</span>
                          <span className="ml-auto text-forest tabnum font-medium" data-testid={`order-item-${i}-line`}>
                            {money(it.line_total || (it.unit_price || 0) * (it.qty || 1))}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section icon={ClipboardList} title="Price breakdown">
                <BreakdownRow label="Subtotal" value={money(subtotal)} testid="order-subtotal" />
                {discount > 0 && (
                  <BreakdownRow
                    label={`Discount${order.coupon_code ? ` · ${order.coupon_code}` : ""}${order.coupon?.kind === "percent" ? ` (${order.coupon.value}%)` : ""}`}
                    value={`− ${money(discount)}`}
                    tone="gold"
                    testid="order-discount"
                  />
                )}
                {shipping > 0 && <BreakdownRow label="Shipping" value={money(shipping)} testid="order-shipping" />}
                {tax > 0 && <BreakdownRow label="Tax" value={money(tax)} testid="order-tax" />}
                <BreakdownRow label="Website total" value={money(calculatedTotal)} bold testid="order-calc-total" />
                {paid != null && (
                  <BreakdownRow
                    label="Actual paid via PayPal"
                    value={paidMoney(paid)}
                    bold
                    tone={paidMismatch ? "amber" : "emerald"}
                    testid="order-paid-total"
                  />
                )}
                {refunded > 0 && (
                  <BreakdownRow label="Refunded" value={`− ${paidMoney(refunded)}`} tone="red" testid="order-refunded" />
                )}
                {refunded > 0 && paid != null && (
                  <BreakdownRow label="Remaining" value={paidMoney(Math.max(0, paid - refunded))} testid="order-remaining" />
                )}
              </Section>

              <Section icon={CreditCard} title="Payment">
                <Row label="Gateway" value={(order.gateway || "").toUpperCase() || "—"} />
                <Row label="Payment status" value={order.payment_status || "pending"} />
                <Row label="PayPal Order ID" value={order.gateway_order_id} mono />
                <Row label="PayPal Capture ID" value={order.gateway_payment_id} mono />
                <Row label="Paid at" value={order.paid_at ? new Date(order.paid_at).toLocaleString() : "—"} />
                {order.paypal_payer_email && <Row label="PayPal payer" value={`${order.paypal_payer_name || ""} <${order.paypal_payer_email}>`} />}
              </Section>

              <Section icon={CreditCard} title="Refund">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={`Refunded (${order.paid_currency || cur})`}>
                    <BlurToSave
                      initial={String(order.refunded_amount ?? "")}
                      onSave={(v) => patchField({ refunded_amount: Number(v) || 0 })}
                      testid="order-drawer-refund-amount"
                      placeholder="0.00"
                      type="number"
                    />
                  </Field>
                  <Field label="Refund ID">
                    <BlurToSave
                      initial={order.refund_id || ""}
                      onSave={(v) => patchField({ refund_id: v })}
                      testid="order-drawer-refund-id"
                      placeholder="PayPal refund reference"
                    />
                  </Field>
                  <Field label="Refund date">
                    <BlurToSave
                      initial={order.refund_date || ""}
                      onSave={(v) => patchField({ refund_date: v })}
                      testid="order-drawer-refund-date"
                      placeholder="YYYY-MM-DD"
                      type="date"
                    />
                  </Field>
                  <Field label="Refund note">
                    <BlurToSave
                      initial={order.refund_note || ""}
                      onSave={(v) => patchField({ refund_note: v })}
                      testid="order-drawer-refund-note"
                      placeholder="Reason / customer request"
                    />
                  </Field>
                </div>
                {paid != null && refunded > 0 && (
                  <div className="mt-3 text-forest/70 text-xs">
                    Net effective amount to customer: <b className="text-forest tabnum">{paidMoney(Math.max(0, paid - refunded))}</b>.
                  </div>
                )}
              </Section>

              <Section icon={Package} title="Fulfillment">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Status">
                    <select
                      value={order.fulfillment_status || "pending"}
                      onChange={(e) => patchField({ fulfillment_status: e.target.value })}
                      data-testid="order-drawer-fulfill"
                      className="w-full bg-transparent border border-hairline rounded-full px-3 py-2 text-sm text-forest focus:border-forest focus:outline-none"
                    >
                      {FULFILL_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </Field>
                  <Field label="Courier">
                    <select
                      value={order.courier_name || ""}
                      onChange={(e) => patchField({ courier_name: e.target.value })}
                      data-testid="order-drawer-courier"
                      className="w-full bg-transparent border border-hairline rounded-full px-3 py-2 text-sm text-forest focus:border-forest focus:outline-none"
                    >
                      {COURIERS.map((c) => <option key={c || "none"} value={c}>{c || "— none —"}</option>)}
                    </select>
                  </Field>
                  <Field label="Tracking / AWB">
                    <BlurToSave
                      initial={order.tracking_number || ""}
                      onSave={(v) => patchField({ tracking_number: v })}
                      testid="order-drawer-tracking"
                      placeholder="AWB or tracking number"
                    />
                  </Field>
                  <Field label="Est. delivery">
                    <BlurToSave
                      initial={order.estimated_delivery || ""}
                      onSave={(v) => patchField({ estimated_delivery: v })}
                      testid="order-drawer-eta"
                      placeholder="YYYY-MM-DD"
                      type="date"
                    />
                  </Field>
                </div>
              </Section>

              <Section icon={Star} title="Status timeline">
                <ol className="border-l border-hairline pl-4 space-y-3">
                  {(order.status_history || []).slice().reverse().map((h, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full bg-gold border border-champagne" />
                      <div className="text-forest text-sm">{h.status.replace(/_/g, " ")}</div>
                      <div className="text-forest/50 text-xs">{h.at ? new Date(h.at).toLocaleString() : ""}</div>
                      {h.note && <div className="text-forest/70 text-xs italic">{h.note}</div>}
                    </li>
                  ))}
                  {!(order.status_history || []).length && (
                    <li className="text-forest/50 text-sm italic">No status changes yet.</li>
                  )}
                </ol>
              </Section>

              <Section icon={ClipboardList} title="Internal admin notes">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Not visible to the customer — for your team only."
                  data-testid="order-drawer-notes"
                  className="w-full bg-white border border-hairline rounded-lg p-3 text-sm text-forest min-h-[100px] focus:border-forest focus:outline-none"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={saveNotes}
                    disabled={savingNotes}
                    data-testid="order-drawer-notes-save"
                    className="inline-flex items-center gap-2 overline text-white bg-forest hover:bg-black rounded-full px-4 py-2 text-[10px] tracking-[2px] disabled:opacity-50"
                  >
                    {savingNotes ? <Loader2 size={12} className="animate-spin" /> : null}
                    Save note
                  </button>
                </div>
              </Section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="bg-white border border-hairline rounded-lg p-4">
    <div className="flex items-center gap-2 overline text-forest/70 text-[10px] tracking-[3px] mb-3">
      {Icon && <Icon size={12} strokeWidth={1.6} />}
      <span>{title}</span>
    </div>
    <div>{children}</div>
  </section>
);

const Row = ({ label, value, mono }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <span className="text-forest/60 text-xs shrink-0">{label}</span>
    <span className={`text-forest text-sm text-right break-all ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
  </div>
);

const BreakdownRow = ({ label, value, bold, tone, testid }) => {
  const toneClass =
    tone === "gold" ? "text-gold" :
    tone === "emerald" ? "text-emerald-700" :
    tone === "amber" ? "text-amber-700" :
    tone === "red" ? "text-red-700" :
    "text-forest";
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-hairline last:border-0" data-testid={testid}>
      <span className={`text-sm ${bold ? "font-medium text-forest" : "text-forest/70"}`}>{label}</span>
      <span className={`tabnum text-sm ${bold ? "font-display text-base" : ""} ${toneClass}`}>{value}</span>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="overline text-forest/60 text-[10px] tracking-[2px] mb-1">{label}</div>
    {children}
  </div>
);

const BlurToSave = ({ initial = "", onSave, testid, placeholder, type = "text" }) => {
  const [v, setV] = useState(initial);
  return (
    <input
      type={type}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== initial && onSave?.(v.trim())}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-transparent border border-hairline rounded-full px-3 py-2 text-sm text-forest focus:border-forest focus:outline-none"
    />
  );
};

const formatAddress = (a = {}) =>
  [a.name, a.line1, a.line2, [a.city, a.state, a.postal_code].filter(Boolean).join(", "), a.country]
    .filter(Boolean).join(" · ");

export default OrderDetailDrawer;
